"use client";

import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePostHog } from "posthog-js/react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useFieldArray, useForm } from "react-hook-form";

import type { CreateSellToUsFormSubmissionSchema } from "@gsa/core/schemas/formSubmission";
import { createSellToUsFormSubmissionSchema } from "@gsa/core/schemas/formSubmission";
import { FormType } from "@gsa/db/schema";
import { Button, LoadingButton } from "@gsa/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@gsa/ui/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@gsa/ui/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@gsa/ui/components/ui/form";
import { toast } from "@gsa/ui/components/ui/sonner";
import { PlusIcon, XIcon } from "@gsa/ui/icons";
import { Input } from "@gsa/ui/input";
import { Textarea } from "@gsa/ui/textarea";

import { createSellToUsFormSubmission } from "~/lib/actions";

export default function SellToUsForm() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const posthog = usePostHog();
  const [sent, setSent] = useState(false);
  const [open, setOpen] = useState(false);

  const form = useForm<CreateSellToUsFormSubmissionSchema>({
    resolver: zodResolver(createSellToUsFormSubmissionSchema),

    defaultValues: {
      type: FormType.SellToUs,
      data: {
        items: [
          {
            name: "",
            description: "",
            quantity: 1,
          },
        ],
      },
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "data.items",
  });

  const onSubmit = async (input: CreateSellToUsFormSubmissionSchema) => {
    if (!executeRecaptcha) {
      console.log("Execute recaptcha not available yet");
      toast.error(
        "Execute recaptcha not available yet likely meaning key not recaptcha key not set",
      );
      return;
    }

    const result = await createSellToUsFormSubmission({
      ...input,
      recaptchaToken: await executeRecaptcha("sell_to_us_form_submit"),
    });

    if (result?.success) {
      setSent(true);
      setOpen(true);
      //   toast(result?.message);
      //   router.push(`/dashboard/store/categories/${result.data?.id}`);
      posthog.identify(
        input.data.email ?? input.data.name, // Replace 'distinct_id' with your user's unique identifier
        input, // optional: set additional user properties
      );
      setTimeout(() => {
        setSent(false);
        setOpen(false);
      }, 5000);
    } else {
      toast(result?.message);
    }
  };

  if (sent) {
    return (
      <Dialog
        open={open}
        onOpenChange={() => {
          setOpen(false);
          setSent(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Thank you</DialogTitle>
            <DialogDescription>
              Your message has been received. We'll get back to you shortly.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sell your items</CardTitle>
        <CardDescription>
          Discover the Hidden Value in Your Treasures with Gold Standard
          Auctions
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="relative grid gap-8 "
          >
            <FormField
              control={form.control}
              name="data.name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      type="text"
                      autoComplete="name"
                      placeholder="Enter your name"
                      title="Enter your name"
                      required
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="data.email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      title="Enter your email"
                      type="email"
                      autoComplete="email"
                      placeholder="Enter your email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="data.phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      title="Enter your phone number"
                      type="tel"
                      autoComplete="tel"
                      placeholder="Enter your phone number"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <div className="text-sm font-medium">Items</div>
              <ul className="space-y-4">
                {fields.map((item, index) => (
                  <li key={item.id} className="rounded-md border">
                    <div className="flex items-center divide-x ">
                      <Input
                        {...form.register(`data.items.${index}.name`)}
                        type="text"
                        title="Enter the name of your item"
                        className="rounded-none border-0  "
                        placeholder="What would you like to sell?"
                      />

                      <Input
                        {...form.register(`data.items.${index}.quantity`)}
                        onChange={(e) => Number.parseInt(e.target.value)}
                        type="number"
                        min={1}
                        step={1}
                        title="Enter the quantity of your item"
                        className=" w-16 flex-shrink rounded-none border-0 border-l ring-0"
                        placeholder="Quantity"
                      />

                      {/* <Controller
                      render={({ field }) => <input {...field} />}
                      name={`data.items.${index}.name`}
                      control={form.control}
                    /> */}

                      <Button
                        size="icon"
                        type="button"
                        variant="ghost"
                        className="flex-shrink rounded-none "
                        onClick={() => remove(index)}
                      >
                        <XIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    <Textarea
                      {...form.register(`data.items.${index}.description`)}
                      title="Enter the description of your item"
                      className="rounded-none border-b-0  border-l-0   border-r-0 border-t"
                      placeholder="Description"
                    />
                  </li>
                ))}
              </ul>
            </div>

            <Button
              variant="outline"
              type="button"
              onClick={() => append({ name: "", quantity: 1, description: "" })}
            >
              <PlusIcon className="h-4 w-4" /> Add Another Item
            </Button>

            <FormField
              control={form.control}
              name="data.message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Anything else we should know?</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value ?? ""}
                      placeholder="Enter your message"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <LoadingButton
              loading={form.formState.isSubmitting}
              disabled={form.formState.isSubmitting}
            >
              Submit
            </LoadingButton>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
